import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { SuggestResponse, HlSpan } from '../services/search';
import { renderHighlighted } from '../services/search';
import { AdvancedSearchHint } from './AdvancedSearchHint';

interface Props {
  data: SuggestResponse | null;
  loading: boolean;
  errorState: 'none' | 'building' | 'rate-limited' | 'network';
  query: string;
  activeIdx: number;
  onHover: (idx: number) => void;
  onAfterClick: () => void;
  /** If provided, replaces the default keyboard-hint footer with an "advanced search" link. */
  advancedSearchHref?: string;
}

function HL({ source, spans }: { source: string | null | undefined; spans: HlSpan[] | undefined }) {
  if (!source) return null;
  const pieces = renderHighlighted(source, spans ?? []);
  return (
    <>
      {pieces.map((p, i) =>
        p.hl ? (
          // 高亮用「加粗 + navy 色」而非 <mark> 背景盒：避免 iOS Safari 在 truncate(省略号)容器内
          // 渲染「带背景/内边距的内联元素」时崩溃白屏的已知坑；同时与搜索结果页高亮风格一致。
          <span key={i} className="font-semibold text-navy">
            {p.text}
          </span>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  );
}

function Row({
  active,
  onHover,
  onAfterClick,
  children,
  to,
  state,
}: {
  active: boolean;
  onHover: () => void;
  onAfterClick: () => void;
  children: ReactNode;
  to: string;
  state?: unknown;
}) {
  return (
    <Link
      to={to}
      state={state}
      onMouseEnter={onHover}
      onClick={onAfterClick}
      className={`flex items-center justify-between gap-3 px-3 py-2 text-[13px] leading-tight transition-colors ${
        active ? 'bg-surface-muted' : 'hover:bg-canvas'
      }`}
    >
      {children}
    </Link>
  );
}

export function SearchSuggestDropdown({
  data,
  loading,
  errorState,
  query,
  activeIdx,
  onHover,
  onAfterClick,
  advancedSearchHref,
}: Props) {
  if (errorState === 'building') {
    return (
      <div className="border border-line bg-surface px-4 py-3 text-[12.5px] text-fg-subtle">
        搜索索引正在构建，约 2 秒后自动重试…
      </div>
    );
  }
  if (errorState === 'rate-limited') {
    return (
      <div className="border border-line bg-surface px-4 py-3 text-[12.5px] text-fg-subtle">
        请求过于频繁，请稍候再试。
      </div>
    );
  }
  if (errorState === 'network') {
    return (
      <div className="border border-line bg-surface px-4 py-3 text-[12.5px] text-fg-subtle">
        搜索暂时不可用，请稍后重试。
      </div>
    );
  }
  if (loading && !data) {
    return (
      <div className="border border-line bg-surface px-4 py-3">
        <div className="h-3 w-2/3 animate-pulse bg-surface-muted" />
        <div className="mt-2 h-3 w-1/2 animate-pulse bg-surface-muted" />
        <div className="mt-2 h-3 w-3/5 animate-pulse bg-surface-muted" />
      </div>
    );
  }
  if (!data) return null;
  if (data.terms.length === 0) {
    return (
      <div className="border border-line bg-surface px-4 py-3 text-[12.5px] text-fg-subtle">
        没有匹配「{query}」的结果。
      </div>
    );
  }

  return (
    <div
      className="border border-line bg-surface shadow-sm"
      onMouseDown={(e) => e.preventDefault()}
    >
      <div>
        {data.terms.map((t, i) => (
          <Row
            key={'t' + i + t.text}
            // 不再用整条词条覆盖查询：仍按「用户已输入字符」搜索(保留宽泛结果)；
            // 点击的词条经 navigation state 传给结果页，把其对应数据集顶到最前(一次性，后续操作自动清除)。
            to={`/search?q=${encodeURIComponent(query || t.text)}`}
            state={{ featured: t.text }}
            active={activeIdx === i}
            onHover={() => onHover(i)}
            onAfterClick={onAfterClick}
          >
            {/* 不用 truncate(text-overflow:ellipsis)：截断含「数字+连字符」(如 2024-2007)的混合文本
                会触发 iOS Safari 渲染崩溃白屏；改为正常换行，长词条折行显示。 */}
            <span className="min-w-0 flex-1 break-words text-fg">
              <HL source={t.text} spans={t.hl} />
            </span>
          </Row>
        ))}
      </div>
      {advancedSearchHref ? (
        <AdvancedSearchHint
          to={advancedSearchHref}
          onClick={onAfterClick}
          variant="footer"
          trailing={`${data.terms.length} 个词条`}
        />
      ) : (
        <div className="flex justify-between border-t border-line-subtle bg-canvas px-3 py-2 text-[11px] text-fg-subtle">
          <span>↵ 进入高级搜索</span>
          <span>{data.terms.length} 个词条</span>
        </div>
      )}
    </div>
  );
}
